import DesignTokens from '@tableau/design-tokens';
import { PopoverWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { RoutingFilterProject } from '../../types/RoutingFilterProject';
import { ERROR_LIGHT_FALLBACK, focusVisibleOutlineNest } from './routingFilterTableStyles';

export type ProjectsInfoPopoverProps = Readonly<{
  projects: ReadonlyArray<RoutingFilterProject>;
  conflictingProjectIds?: ReadonlySet<string>;
}>;

export function ProjectsInfoPopover(props: ProjectsInfoPopoverProps) {
  const { projects, conflictingProjectIds } = props;
  if (projects.length === 0) {
    return null;
  }

  return (
    <span className={containerClass}>
      <PopoverWidget
        testId='projects-info-popover'
        placement='top'
        width={320}
        showTitleBar={true}
        title={Messages.bridgeSiteSettingsRoutingFilterProjectsInRule()}
        popoverContent={renderPopoverContent(projects, conflictingProjectIds)}
        triggerElement={
          <button
            type='button'
            className={infoButtonClass}
            aria-label={Messages.bridgeSiteSettingsRoutingFilterProjectsInRule()}
          >
            <img src={imgPath('info_14px_small.svg')} alt='' aria-hidden={true} />
          </button>
        }
      />
    </span>
  );
}

function renderPopoverContent(
  projects: ReadonlyArray<RoutingFilterProject>,
  conflictingProjectIds?: ReadonlySet<string>
) {
  return (
    <ul className={projectListClass}>
      {projects.map(p => {
        const isConflict = conflictingProjectIds?.has(p.id) ?? false;
        const isDeleted = p.deleted === true;
        return (
          <li key={p.id} className={isConflict ? conflictItemClass : projectItemClass}>
            {p.displayValue}
            {isConflict && <span className={conflictAnnotationClass}> {Messages.bridgeSiteSettingsRoutingFilterProjectConflictAnnotation()}</span>}
            {isDeleted && !isConflict && <span className={notExistAnnotationClass}> {Messages.bridgeSiteSettingsRoutingFilterProjectNotExist()}</span>}
          </li>
        );
      })}
    </ul>
  );
}

const containerClass = cssClass('projects-info-popover', 'container', {
  display: 'inline-block',
  marginLeft: 4,
  verticalAlign: 'middle',
});

const infoButtonClass = cssClass('projects-info-popover', 'trigger', {
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  padding: 2,
  verticalAlign: 'middle',
  lineHeight: 1,
  borderRadius: TabStyles.Sizing.BorderRadius,
  $nest: focusVisibleOutlineNest,
});

const projectListClass = cssClass('projects-info-popover', 'list', {
  margin: 0,
  padding: '8px 8px',
  paddingLeft: 32,
  listStyleType: 'disc',
});

// Base row: horizontal padding so the text starts inset from the bullet, a 2px
// radius so a conflict row reads as its own rounded pill, and a small vertical
// margin so consecutive conflict pills stay visually separate rather than
// abutting into one block. Non-conflict rows share the exact geometry (minus the
// fill) so every row lines up.
const projectItemClass = cssClass('projects-info-popover', 'item', {
  padding: '3px 8px',
  margin: '3px 0',
  borderRadius: 2,
});

// Conflict row: an error-light rounded pill matching the tree dropdown's conflict
// highlight — one rounded rectangle per row rather than a single block spanning
// several rows. The shared vertical margin keeps adjacent pills apart. No
// boxShadow bleed; the pill sits within the row box.
const conflictItemClass = cssClass('projects-info-popover', 'conflict-item', {
  padding: '3px 8px',
  margin: '3px 0',
  borderRadius: 2,
  // Theme-aware alert fill (light pink / dark maroon) paired with the matching
  // content-on-alert text token, so the project name stays legible on the pill in
  // both themes — the same background/content token pairing InlineAlert uses.
  backgroundColor: applyColor(DesignTokens.color.component.background.alert.default.error.default, ERROR_LIGHT_FALLBACK),
  color: applyColor(DesignTokens.color.component.content.alert.default.error.default, '#ba0517'),
});

const conflictAnnotationClass = cssClass('projects-info-popover', 'conflict', {
  // Sits on the conflict pill's alert fill, so use the content-on-alert token
  // (dark red on the light pill, light pink on the dark maroon) rather than a
  // fixed red that would vanish on the dark-theme fill.
  color: applyColor(DesignTokens.color.component.content.alert.default.error.default, '#ba0517'),
  fontStyle: 'italic',
});

const notExistAnnotationClass = cssClass('projects-info-popover', 'not-exist', {
  color: applyColor(DesignTokens.color.font.default.secondary, '#5c5c5c'),
  fontStyle: 'italic',
});
